// pages/PlynoteHome.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiEdit3, FiUser, FiCreditCard } from 'react-icons/fi';
import { useColorContext } from '../context/colorContext';
import { useLanguageContext } from '../context/languageContext';
import { useNoteDispatch } from '../context/noteContexts/noteContext';
import { useNavigateContext } from '../context/NavigateContext';
import { useUserLimitDispatch, useUserLimitState } from '../context/user_limit_context';
import {
  ImportRecordAudioFeature,
  ImportFileFeature,
} from '../components/features/buttonsImportFeatures/buttonsImportFeatures';
import StyledActionCard from '../components/features/buttonsImportFeatures/StyledActionCard';
import AudioBlock from '../components/features/audioFeatures/audioBlock';
import SmartTextFeature from '../components/features/smartTextFeatures/smartTextFeature';
import SmartFlashCardFeature from '../components/features/smartFlashCardsFeatures/smartFlashCardFeature';
import SmartFlashCardElement from '../components/features/smartFlashCardsFeatures/smartFlashCardElements';

const HOME_LOADING_ANIM_STYLE_ID = 'plynote-home-import-loading-anim';
if (typeof document !== 'undefined' && !document.getElementById(HOME_LOADING_ANIM_STYLE_ID)) {
  const styleEl = document.createElement('style');
  styleEl.id = HOME_LOADING_ANIM_STYLE_ID;
  styleEl.textContent = `
    @keyframes plynoteSpin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
  `;
  document.head.appendChild(styleEl);
}

const toSafeHex = (hex, fallback) => {
  const value = String(hex || '').trim();
  return /^#([A-Fa-f0-9]{6})$/.test(value) ? value : fallback;
};

const hexToRgba = (hex, alpha, fallback) => {
  const safeHex = toSafeHex(hex, fallback);
  const r = parseInt(safeHex.slice(1, 3), 16);
  const g = parseInt(safeHex.slice(3, 5), 16);
  const b = parseInt(safeHex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const waitForNextFrame = () => new Promise((resolve) => {
  if (typeof requestAnimationFrame === 'function') {
    requestAnimationFrame(() => resolve());
    return;
  }
  setTimeout(resolve, 16);
});

const waitMs = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const PlynoteHome = () => {
  const navigate = useNavigate();
  const { openUpgradePopup } = useUserLimitDispatch();
  const { isUploadLimitReached } = useUserLimitState();
  const {
    setIf_note_opened,
    setNoteSourceFile,
    setNoteSourceLink,
    initNewNoteSettings,
  } = useNoteDispatch();
  const { setValuesAsEditorView } = useNavigateContext();
  const { bgPrimary, bgSecondary, textPrimary, textSecondary, activeAccent, userProfile, varkColors } = useColorContext();
  const { t } = useLanguageContext();
  const userName = 'Anis';
  const fullWelcomeText = t('home.welcome');
  const [typedWelcomeText, setTypedWelcomeText] = useState('');
  const [showCursor, setShowCursor] = useState(true);
  const [isArcReady, setIsArcReady] = useState(false);
  const [isDemoPlaying, setIsDemoPlaying] = useState(false);
  const [demoDisplayIndex, setDemoDisplayIndex] = useState(0);
  const [isImportingSource, setIsImportingSource] = useState(false);
  const [importingSourceLabel, setImportingSourceLabel] = useState('source');
  const [importError, setImportError] = useState(null);
  const [viewport, setViewport] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 1200,
    height: typeof window !== 'undefined' ? window.innerHeight : 900,
  });

  const demoSlides = [
    {
      type: 'text',
      text: 'Here is a preview of synchronized content: text, formulas, or images along the timeline.',
    },
    {
      type: 'latex',
      latex: 'We illustrate with the relation: $$E = mc^2$$',
    },
    {
      type: 'image',
      imageSrc: 'https://picsum.photos/900/240',
      imageAlt: 'Visual preview',
    },
  ];
  const currentDemoSlide = demoSlides[demoDisplayIndex] || demoSlides[0];

  useEffect(() => {
    const raf = requestAnimationFrame(() => setIsArcReady(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  useEffect(() => {
    let currentIndex = 0;
    const typingTimer = setInterval(() => {
      currentIndex += 1;
      setTypedWelcomeText(fullWelcomeText.slice(0, currentIndex));
      if (currentIndex >= fullWelcomeText.length) {
        clearInterval(typingTimer);
      }
    }, 42);

    return () => clearInterval(typingTimer);
  }, [fullWelcomeText]);

  useEffect(() => {
    if (typedWelcomeText.length >= fullWelcomeText.length) {
      setShowCursor(false);
      return undefined;
    }

    const cursorTimer = setInterval(() => {
      setShowCursor((prev) => !prev);
    }, 460);

    return () => clearInterval(cursorTimer);
  }, [typedWelcomeText, fullWelcomeText]);

  useEffect(() => {
    const handleResize = () => {
      setViewport({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleImportedContent = async (importedSource) => {
    console.log('[PlynoteHome] handleImportedContent called with:', importedSource);
    console.log('[PlynoteHome] importedSource.type:', importedSource?.type);
    console.log('[PlynoteHome] importedSource.file:', importedSource?.file ? 'present' : 'missing');
    
    if (isUploadLimitReached) {
      openUpgradePopup(t('home.limitReachedMessage'));
      return;
    }
    let sourceType = null;

    if (typeof importedSource === 'string') {
      console.log('[PlynoteHome] Detected string source (link)');
      const linked = setNoteSourceLink(importedSource);
      if (!linked) {
        console.warn('[PlynoteHome] Failed to set source link');
        return;
      }
      sourceType = 'link';
    } else if (importedSource && typeof importedSource === 'object') {
      const payloadType = String(importedSource.type || '').toLowerCase();
      console.log('[PlynoteHome] Detected object source with type:', payloadType);
      console.log('[PlynoteHome] Checking if payloadType in [audio, pdf, image, doc]:', ['audio', 'pdf', 'image', 'doc'].includes(payloadType));
      console.log('[PlynoteHome] importedSource.file truthy?:', !!importedSource.file);

      if ((payloadType === 'audio' || payloadType === 'pdf' || payloadType === 'image' || payloadType === 'doc') && importedSource.file) {
        console.log('[PlynoteHome] ✅ Condition passed, calling setNoteSourceFile');
        const selected = setNoteSourceFile(importedSource.file);
        console.log('[PlynoteHome] setNoteSourceFile returned:', selected);
        if (!selected) {
          console.warn('[PlynoteHome] Failed to set source file');
          return;
        }
        sourceType = payloadType;
        console.log('[PlynoteHome] sourceType set to:', sourceType);
      } else if ((payloadType === 'link' || payloadType === 'youtube') && typeof importedSource.url === 'string') {
        const linked = setNoteSourceLink(importedSource.url);
        if (!linked) {
          console.warn('[PlynoteHome] Failed to set source link from object');
          return;
        }
        sourceType = payloadType;
      } else if (payloadType === 'unknown' || importedSource.text) {
        // Show error message for unsupported files
        console.warn('[PlynoteHome] File type not supported:', importedSource.text || 'Unknown error');
        setImportingSourceLabel('error');
        setIsImportingSource(true);
        await waitMs(500);
        // Show error alert
        if (typeof window !== 'undefined' && window.alert) {
          window.alert(`⚠️ ${importedSource.text || t('home.unsupportedFormat')}`);
        }
        setIsImportingSource(false);
        return;
      }
    }

    if (!sourceType) {
      console.warn('[PlynoteHome] No valid sourceType detected, returning early');
      return;
    }

    const sourceLabelByType = {
      audio: t('home.sourceType.audio'),
      pdf: t('home.sourceType.pdf'),
      image: t('home.sourceType.image'),
      doc: t('home.sourceType.doc'),
      link: t('home.sourceType.link'),
      youtube: t('home.sourceType.youtube'),
    };

    setImportingSourceLabel(sourceLabelByType[sourceType] || 'source');
    setIsImportingSource(true);

    const loadingStart = Date.now();
    const MIN_LOADING_MS = 700;
    // Let React paint the loading overlay before starting heavy/async work.
    await waitForNextFrame();

    console.log('[PlynoteHome] About to call initNewNoteSettings with type:', sourceType);
    try {
      await initNewNoteSettings(sourceType);
      console.log('[PlynoteHome] Successfully initialized new note settings');
      setIsImportingSource(false);
      setImportingSourceLabel('source');
    } catch (error) {
      console.error('[PlynoteHome] import initialization failed:', error);
      const msg = String(error?.message || '');
      const is502 = msg.includes('502');
      const userMessage = is502
        ? t('home.fileTooLargeError')
        : `${t('home.importFailedPrefix')}${msg || t('home.unexpectedError')}`;
      const elapsed = Date.now() - loadingStart;
      if (elapsed < MIN_LOADING_MS) {
        await waitMs(MIN_LOADING_MS - elapsed);
      }
      setImportError(userMessage);
    }
  };

  const handleBlankNoteClick = async () => {
    try {
      await initNewNoteSettings(null);
    } catch (error) {
      console.error('[PlynoteHome] blank note initialization failed:', error);
    }
    setValuesAsEditorView();
  };

  const safeBgPrimary = toSafeHex(bgPrimary, '#111111');
  const safeBgSecondary = toSafeHex(bgSecondary, '#191919');
  const safeAccent = toSafeHex(activeAccent, '#3B82F6');
  const visualColor = toSafeHex(varkColors?.visual, '#7C3AED');
  const auditoryColor = toSafeHex(varkColors?.auditory, '#3B82F6');

  const homeBackground = [
    `radial-gradient(120% 72% at 52% 124%, ${hexToRgba(safeAccent, 0.34, safeAccent)} 0%, ${hexToRgba(safeAccent, 0.16, safeAccent)} 34%, transparent 66%)`,
    `radial-gradient(92% 56% at 86% 9%, ${hexToRgba(safeBgSecondary, 0.55, safeBgSecondary)} 0%, transparent 74%)`,
    `radial-gradient(90% 52% at 14% 12%, ${hexToRgba(safeBgSecondary, 0.46, safeBgSecondary)} 0%, transparent 72%)`,
    `linear-gradient(180deg, ${safeBgPrimary} 0%, ${hexToRgba(safeBgPrimary, 0.98, safeBgPrimary)} 100%)`,
  ].join(', ');

  const isTinyViewport = viewport.width <= 420;
  const isSmallViewport = viewport.width <= 640;
  const isMediumViewport = viewport.width <= 920;
  const isLargeViewport = viewport.width >= 1440;
  const isXlViewport = viewport.width >= 1680;
  const isShortViewport = viewport.height <= 760;
  const importFeatureSize = isTinyViewport ? 'sm' : isSmallViewport ? 'sm' : isMediumViewport ? 'sm' : 'md';

  const homeContentMaxWidth = isTinyViewport ? '100%' : isSmallViewport ? '600px' : isMediumViewport ? '760px' : isXlViewport ? '960px' : isLargeViewport ? '880px' : '820px';
  const homeCardColumnWidth = isTinyViewport ? '100%' : isSmallViewport ? 'minmax(0, 280px)' : isMediumViewport ? 'minmax(0, 300px)' : isXlViewport ? 'minmax(0, 360px)' : isLargeViewport ? 'minmax(0, 340px)' : 'minmax(0, 320px)';
  const homeVerticalGap = isTinyViewport ? '0.7rem' : isSmallViewport ? '0.95rem' : isMediumViewport ? '1.35rem' : isXlViewport ? '2.3rem' : isLargeViewport ? '2rem' : '1.6rem';
  const homeTitleBottom = isTinyViewport ? '0.8rem' : isSmallViewport ? '1rem' : isMediumViewport ? '1.35rem' : isXlViewport ? '2rem' : isLargeViewport ? '1.8rem' : '1.5rem';
  const homeFooterTop = isTinyViewport ? '0.9rem' : isSmallViewport ? '1rem' : isMediumViewport ? '1.25rem' : isXlViewport ? '2rem' : isLargeViewport ? '1.7rem' : '1.4rem';
  const orbitTop = isTinyViewport ? '40%' : isSmallViewport ? '41%' : isXlViewport ? '44%' : isLargeViewport ? '43%' : '42%';
  const orbitOuterSize = isTinyViewport ? 'min(104vw, 420px)' : isSmallViewport ? 'min(96vw, 500px)' : isXlViewport ? 'min(52vw, 720px)' : isLargeViewport ? 'min(58vw, 640px)' : 'min(62vw, 520px)';
  const orbitInnerSize = isTinyViewport ? 'min(84vw, 320px)' : isSmallViewport ? 'min(78vw, 380px)' : isXlViewport ? 'min(36vw, 460px)' : isLargeViewport ? 'min(40vw, 420px)' : 'min(44vw, 380px)';

  // Only the Blank Note action will use StyledActionCard directly
  const importCardProps = useMemo(() => ({
    style: {
      minHeight: isTinyViewport ? 56 : isSmallViewport ? 64 : isMediumViewport ? 72 : isXlViewport ? 92 : isLargeViewport ? 86 : 80,
      padding: isTinyViewport ? '1rem 0.95rem' : isSmallViewport ? '1.05rem 1rem' : isMediumViewport ? '1.2rem 1.1rem' : isXlViewport ? '1.5rem 1.35rem' : isLargeViewport ? '1.4rem 1.25rem' : '1.3rem 1.15rem',
      borderRadius: isTinyViewport ? '1.15rem' : '1.25rem',
      boxShadow: '0 10px 30px rgba(2,6,23,0.35)',
      margin: 0,
    },
    titleStyle: {
      fontSize: isTinyViewport ? '1rem' : isSmallViewport ? '1.08rem' : isMediumViewport ? '1.16rem' : isXlViewport ? '1.34rem' : isLargeViewport ? '1.28rem' : '1.22rem',
    },
    descStyle: {
      fontSize: isTinyViewport ? '0.82rem' : isSmallViewport ? '0.88rem' : isMediumViewport ? '0.94rem' : isXlViewport ? '1.06rem' : isLargeViewport ? '1.0rem' : '0.98rem',
    },
  }), [isTinyViewport, isSmallViewport, isMediumViewport, isLargeViewport, isXlViewport]);

  const smartTextDemoMarkdown = `## Mechanics - Energy and Motion

### Definition
📘 The total mechanical energy is <mark>the sum</mark> of kinetic energy and potential energy.

### Key relationship
⚖️ In this course, we preserve the formula index <mark>[[FINDEX:0]]</mark> without modifying it.

### Key takeaway
> 📌 A change in altitude modifies potential energy, and therefore the overall equilibrium.

## Mechanics - Energy and Motion

### Definition
📘 The total mechanical energy is <mark>the sum</mark> of kinetic energy and potential energy.

### Key relationship
⚖️ In this course, we preserve the formula index <mark>[[FINDEX:0]]</mark> without modifying it.

### Key takeaway
> 📌 A change in altitude modifies potential energy, and therefore the overall equilibrium.

## Mechanics - Energy and Motion

### Definition
📘 The total mechanical energy is <mark>the sum</mark> of kinetic energy and potential energy.

### Key relationship
⚖️ In this course, we preserve the formula index <mark>[[FINDEX:0]]</mark> without modifying it.

### Key takeaway
> 📌 A change in altitude modifies potential energy, and therefore the overall equilibrium.

## Mechanics - Energy and Motion

### Definition
📘 The total mechanical energy is <mark>the sum</mark> of kinetic energy and potential energy.

### Key relationship
⚖️ In this course, we preserve the formula index <mark>[[FINDEX:0]]</mark> without modifying it.

### Key takeaway
> 📌 A change in altitude modifies potential energy, and therefore the overall equilibrium.

## Mechanics - Energy and Motion

### Definition
📘 The total mechanical energy is <mark>the sum</mark> of kinetic energy and potential energy.

### Key relationship
⚖️ In this course, we preserve the formula index <mark>[[FINDEX:0]]</mark> without modifying it.

### Key takeaway
> 📌 A change in altitude modifies potential energy, and therefore the overall equilibrium.

## Mechanics - Energy and Motion

### Definition
📘 The total mechanical energy is <mark>the sum</mark> of kinetic energy and potential energy.

### Key relationship
⚖️ In this course, we preserve the formula index <mark>[[FINDEX:0]]</mark> without modifying it.

### Key takeaway
> 📌 A change in altitude modifies potential energy, and therefore the overall equilibrium.
`;

  const exampleSmartFlashcardResult = {
    success: true,
    parts: [
      {
        partIndex: 0,
        title: 'Exemple de flashcards',
        flashcards: [
          {
            show: "Quelle est la relation d\u00e9montr e9e par la formule principal ?",
            hide: "La formule principale est $E = mc^2$, qui relie l\u2019energie et la masse.",
          },
          {
            show: "Comment Plynote conserve-t-il l\u2019index de formule ?",
            hide: "Plynote conserve les placeholders comme [[FINDEX:0]] sans les modifier.",
          },
        ],
      },
    ],
  };

  return (
    <div
      style={{
        width: '100%',
        height: '100svh',
        minHeight: '100svh',
        background: `linear-gradient(135deg, ${safeBgPrimary} 0%, ${hexToRgba(visualColor, 0.05, safeBgPrimary)} 20%, ${hexToRgba(auditoryColor, 0.05, safeBgPrimary)} 45%, ${hexToRgba(safeAccent, 0.06, safeBgPrimary)} 70%, ${safeBgPrimary} 100%)`,
        overflow: 'hidden',
        overscrollBehavior: 'none',
      }}
    >
      <main
        className="plynote-home-main"
        style={{
          height: '100%',
          minHeight: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: homeVerticalGap,
          padding: isTinyViewport ? '0.6rem' : isXlViewport ? '2.2rem 2.5rem' : isLargeViewport ? '1.7rem 2rem' : 'clamp(0.8rem, 2vw, 1.5rem)',
          overflow: 'hidden',
          backdropFilter: 'saturate(112%)',
          background: homeBackground,
          position: 'relative',
        }}
      >
        <div
          aria-hidden="true"
          className="plynote-home-orbit plynote-home-orbit-1"
          style={{
            position: 'absolute',
            left: '50%',
            top: orbitTop,
            width: orbitOuterSize,
            height: orbitOuterSize,
            borderRadius: '50%',
            border: `2px solid ${hexToRgba(safeAccent, 0.34, safeAccent)}`,
            borderRightColor: 'transparent',
            borderBottomColor: 'transparent',
            transform: isArcReady ? 'translate(-50%, -50%) translateY(18px) rotate(14deg) scale(1)' : 'translate(-50%, -50%) translateY(36px) rotate(-8deg) scale(0.92)',
            opacity: isArcReady ? 0.92 : 0,
            filter: isArcReady ? `drop-shadow(0 0 22px ${hexToRgba(safeAccent, 0.42, safeAccent)})` : 'none',
            transition: 'transform 980ms cubic-bezier(0.22, 1, 0.36, 1), opacity 840ms ease, filter 980ms ease',
            pointerEvents: 'none',
          }}
        />

        <div
          aria-hidden="true"
          className="plynote-home-orbit plynote-home-orbit-2"
          style={{
            position: 'absolute',
            left: '50%',
            top: orbitTop,
            width: orbitInnerSize,
            height: orbitInnerSize,
            borderRadius: '50%',
            border: `1px solid ${hexToRgba(safeAccent, 0.22, safeAccent)}`,
            borderLeftColor: 'transparent',
            borderTopColor: 'transparent',
            transform: isArcReady ? 'translate(-50%, -50%) translateY(0) rotate(-16deg) scale(1)' : 'translate(-50%, -50%) translateY(18px) rotate(-2deg) scale(0.95)',
            opacity: isArcReady ? 0.75 : 0,
            transition: 'transform 1100ms cubic-bezier(0.16, 1, 0.3, 1), opacity 900ms ease',
            pointerEvents: 'none',
          }}
        />

        <div style={{ textAlign: 'center', marginBottom: homeTitleBottom, marginTop: 0, position: 'relative', zIndex: 2 }}>
          <h1
            style={{
              color: textPrimary,
              fontSize: isTinyViewport ? 'clamp(1.4rem, 7vw, 1.75rem)' : isSmallViewport ? 'clamp(1.6rem, 5.8vw, 2.1rem)' : isXlViewport ? 'clamp(2.6rem, 2.4vw, 4rem)' : isLargeViewport ? 'clamp(2.4rem, 2.2vw, 3.6rem)' : 'clamp(1.95rem, 3.2vw, 3rem)',
              fontWeight: 600,
              letterSpacing: '-0.02em',
              margin: 0,
              minHeight: '1.2em',
            }}
          >
            {typedWelcomeText}
            <span style={{ opacity: showCursor ? 1 : 0, transition: 'opacity 120ms linear' }}>|</span>
          </h1>
          <p style={{ color: textSecondary, fontSize: isTinyViewport ? '0.9rem' : isSmallViewport ? '1rem' : isXlViewport ? 'clamp(1.05rem, 0.95vw, 1.18rem)' : isLargeViewport ? 'clamp(1.02rem, 0.95vw, 1.12rem)' : 'clamp(0.98rem, 1.1vw, 1.04rem)', marginTop: '0.5rem', maxWidth: isTinyViewport ? 420 : isSmallViewport ? 620 : isXlViewport ? 960 : isLargeViewport ? 900 : 820, padding: '0 0.25rem', lineHeight: 1.45 }}>
            {userProfile === 'visual' && t('home.visualProfile')}
            {userProfile === 'auditory' && t('home.auditoryProfile')}
            {userProfile === 'reading' && t('home.readingProfile')}
            {userProfile === 'kinesthetic' && t('home.kinestheticProfile')}
          </p>
        </div>

        <div style={{ width: '100%', maxWidth: homeContentMaxWidth, margin: '0 auto' }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: isTinyViewport ? '1fr' : `repeat(2, ${homeCardColumnWidth})`,
              justifyContent: 'center',
              alignItems: 'stretch',
              columnGap: isTinyViewport ? '0.6rem' : isSmallViewport ? '1rem' : isXlViewport ? '2.4rem' : isLargeViewport ? '2.1rem' : '1.6rem',
              rowGap: 0,
              width: '100%',
            }}
          >
            <div style={{ minWidth: 0, display: 'flex', justifyContent: 'center', width: '100%', maxWidth: isTinyViewport ? '100%' : isXlViewport ? '360px' : isLargeViewport ? '340px' : 'min(280px, 100%)', margin: '0 auto' }}>
              <ImportRecordAudioFeature
                size={importFeatureSize}
                allImportButtonProps={{
                  ...importCardProps,
                  style: {
                    ...importCardProps.style,
                    width: '100%',
                  },
                }}
                onAudioAction={handleImportedContent}
              />
            </div>
            <div style={{ minWidth: 0, display: 'flex', justifyContent: 'center', width: '100%', maxWidth: isTinyViewport ? '100%' : isXlViewport ? '360px' : isLargeViewport ? '340px' : 'min(280px, 100%)', margin: '0 auto' }}>
              <ImportFileFeature
                size={importFeatureSize}
                allImportButtonProps={{
                  ...importCardProps,
                  style: {
                    ...importCardProps.style,
                    width: '100%',
                  },
                }}
                onFileImport={handleImportedContent}
              />
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'center', width: '100%', marginTop: isTinyViewport ? '0.5rem' : isXlViewport ? '1.35rem' : isLargeViewport ? '1.15rem' : '0.7rem' }}>
            <StyledActionCard
              icon={<FiEdit3 size={isTinyViewport ? 16 : isSmallViewport ? 18 : isMediumViewport ? 20 : isXlViewport ? 26 : isLargeViewport ? 24 : 22} color={activeAccent} strokeWidth={1.5} />}
              title={t('home.blankNote')}
              desc={t('home.startFromScratch')}
              highlight={false}
              onClick={handleBlankNoteClick}
              style={{ minHeight: isTinyViewport ? 60 : isSmallViewport ? 68 : isMediumViewport ? 76 : isXlViewport ? 96 : isLargeViewport ? 90 : 84, padding: isTinyViewport ? '0.95rem 0.9rem' : isSmallViewport ? '1.05rem 1rem' : isMediumViewport ? '1.18rem 1.1rem' : isXlViewport ? '1.5rem 1.35rem' : isLargeViewport ? '1.4rem 1.25rem' : '1.3rem 1.15rem', borderRadius: isTinyViewport ? '1rem' : '1.2rem', boxShadow: '0 14px 40px rgba(2,6,23,0.32)', color: textPrimary, fontSize: isTinyViewport ? '0.95rem' : isSmallViewport ? '1.04rem' : isMediumViewport ? '1.12rem' : isXlViewport ? '1.28rem' : isLargeViewport ? '1.22rem' : '1.18rem', fontWeight: 600, margin: 0, width: isTinyViewport ? '100%' : isXlViewport ? '420px' : isLargeViewport ? '380px' : 'min(320px, 100%)' }}
              titleStyle={{ fontSize: isTinyViewport ? '1.02rem' : isSmallViewport ? '1.12rem' : isMediumViewport ? '1.2rem' : isXlViewport ? '1.36rem' : isLargeViewport ? '1.32rem' : '1.28rem' }}
              descStyle={{ fontSize: isTinyViewport ? '0.88rem' : isSmallViewport ? '0.94rem' : isMediumViewport ? '1.0rem' : isXlViewport ? '1.12rem' : isLargeViewport ? '1.08rem' : '1.04rem' }}
            />
          </div>
        </div>

        <div style={{ marginTop: homeFooterTop, textAlign: 'center' }}>
          <p style={{ color: textSecondary, fontSize: isTinyViewport ? '0.88rem' : '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.6rem' }}>
            <FiUser size={isTinyViewport ? 14 : 16} /> {t('home.tagline')}
          </p>
        </div>
      </main>

      {(isImportingSource || importError) ? (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 20,
            background: hexToRgba(safeBgPrimary, 0.82, safeBgPrimary),
            backdropFilter: 'blur(8px)',
          }}
        >
          <div
            style={{
              width: 'min(680px, 94vw)',
              borderRadius: 22,
              border: `1px solid ${importError ? 'rgba(239,68,68,0.28)' : hexToRgba(textPrimary, 0.12, '#FFFFFF')}`,
              background: `linear-gradient(180deg, ${hexToRgba(safeBgPrimary, 0.98, safeBgPrimary)} 0%, ${hexToRgba(safeBgSecondary, 0.98, safeBgSecondary)} 100%)`,
              boxShadow: `0 40px 90px ${hexToRgba('#02040F', 0.48, '#02040F')}, 0 0 0 1px ${importError ? 'rgba(239,68,68,0.14)' : hexToRgba(safeAccent, 0.16, safeAccent)}`,
              padding: '32px 28px',
              textAlign: 'center',
            }}
          >
            {importError ? (
              <>
                <div style={{ fontSize: 36, marginBottom: 14 }}>⚠️</div>
                <h2 style={{ margin: 0, color: textPrimary, fontSize: 'clamp(1.1rem, 2.8vw, 1.35rem)', fontWeight: 600 }}>
                  {t('home.importFailed')}
                </h2>
                <p style={{ margin: '10px 0 20px', color: textSecondary, fontSize: '0.93rem', lineHeight: 1.55 }}>
                  {importError}
                </p>
                <button
                  type="button"
                  onClick={() => { setImportError(null); setIsImportingSource(false); setImportingSourceLabel('source'); }}
                  style={{
                    padding: '9px 28px',
                    borderRadius: 999,
                    border: 'none',
                    background: safeAccent,
                    color: '#fff',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  {t('home.close')}
                </button>
              </>
            ) : (
              <>
                <div
                  style={{
                    width: 68,
                    height: 68,
                    margin: '0 auto 18px',
                    borderRadius: '50%',
                    border: `4px solid ${hexToRgba(safeAccent, 0.22, safeAccent)}`,
                    borderTopColor: safeAccent,
                    animation: 'plynoteSpin 0.85s linear infinite',
                    boxShadow: `0 8px 30px ${hexToRgba(safeAccent, 0.12, safeAccent)}`,
                  }}
                />
                <h2 style={{ margin: 0, color: textPrimary, fontSize: 'clamp(1.1rem, 2.8vw, 1.45rem)', fontWeight: 600 }}>
                  {t('home.importInProgress')}
                </h2>
                <p style={{ margin: '10px 0 0', color: textSecondary, fontSize: '0.98rem', lineHeight: 1.5 }}>
                  {t('home.importPreparingMessage', { label: importingSourceLabel })}
                </p>
              </>
            )}
          </div>
        </div>
      ) : null}

      <style>
        {`
          @media (max-width: 1024px) {
            .plynote-home-main {
              justify-content: flex-start !important;
              padding-top: clamp(1rem, 3.2vw, 1.7rem) !important;
            }

            .plynote-home-orbit-1 {
              width: min(88vw, 620px) !important;
              height: min(88vw, 620px) !important;
            }

            .plynote-home-orbit-2 {
              width: min(72vw, 500px) !important;
              height: min(72vw, 500px) !important;
            }
          }

          @media (max-width: 640px) {
            .plynote-home-main {
              min-height: 100svh;
              gap: 0.2rem;
            }

            .plynote-home-orbit-1 {
              width: min(104vw, 420px) !important;
              height: min(104vw, 420px) !important;
              top: 40% !important;
              opacity: 0.72 !important;
            }

            .plynote-home-orbit-2 {
              width: min(84vw, 320px) !important;
              height: min(84vw, 320px) !important;
              top: 40% !important;
              opacity: 0.45 !important;
            }

            .plynote-home-main h1 {
              font-size: clamp(1.35rem, 7.2vw, 1.95rem) !important;
            }
          }

          @media (max-width: 480px) {
            .plynote-home-main {
              padding-left: 0.9rem !important;
              padding-right: 0.9rem !important;
            }

            .plynote-home-orbit-2 {
              display: none !important;
            }
          }

          @media (min-width: 1440px) {
            .plynote-home-main {
              gap: 2rem;
            }

            .plynote-home-orbit-1 {
              width: min(58vw, 660px) !important;
              height: min(58vw, 660px) !important;
              top: 43% !important;
            }

            .plynote-home-orbit-2 {
              width: min(40vw, 460px) !important;
              height: min(40vw, 460px) !important;
              top: 43% !important;
            }
          }

          @media (min-width: 1680px) {
            .plynote-home-main {
              gap: 2.4rem;
            }

            .plynote-home-orbit-1 {
              width: min(52vw, 760px) !important;
              height: min(52vw, 760px) !important;
              top: 44% !important;
            }

            .plynote-home-orbit-2 {
              width: min(34vw, 520px) !important;
              height: min(34vw, 520px) !important;
              top: 44% !important;
            }
          }
        `}
      </style>
    </div>
  );
};

export default PlynoteHome;